import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Show } from '../../types/Show';
import ShowTile from '../ShowTile';
import CarouselArrow from './CarouselArrow';
import { useCarousel } from '../../hooks/ui/useCarousel';
import { useScrollAwareTap } from '../../hooks/ui/useScrollAwareTap';

export interface HorizontalShowCarouselProps {
  shows: Show[];
  title?: string;
  showsPerView?: {
    mobile: number;
    tablet: number;
    desktop: number;
  };
  scrollAmount?: number;
  showArrows?: boolean;
  className?: string;
  arrowClassName?: string;
  onShowClick?: (show: Show) => void;
  testId?: string;
}

/**
 * ScrollAwareShowTile - Uses interact.js tap events to distinguish between taps and scrolls
 * Properly configured to allow native scrolling while detecting genuine taps
 */
const ScrollAwareShowTile: React.FC<{
  show: Show;
  onShowClick?: (show: Show) => void;
  className?: string;
  style?: React.CSSProperties;
}> = ({ show, onShowClick, className, style }) => {
  const navigate = useNavigate();

  // Default navigation handler if none provided
  const handleShowClick = useCallback(() => {
    if (onShowClick) {
      onShowClick(show);
    } else {
      // Default behavior: navigate to show details
      navigate(`/show/${show.show_id}`);
    }
  }, [show, onShowClick, navigate]);

  const { touchHandlers } = useScrollAwareTap({
    onTap: handleShowClick,
    scrollThreshold: 12,
    timeThreshold: 500
  });

  const handleClick = useCallback(() => {
    handleShowClick();
  }, [handleShowClick]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleShowClick();
    }
  }, [handleShowClick]);

  return (
    <div
      className={`${className} focus:outline-none focus:ring-2 focus:ring-red-500/50 focus:ring-offset-2 focus:ring-offset-gray-900 rounded-xl transition-all duration-200`}
      style={{
        ...style,
        // Critical: Allow horizontal scrolling while enabling tap detection
        touchAction: 'pan-x pan-y',
        // Prevent text selection during interactions
        userSelect: 'none',
        // Ensure proper cursor
        cursor: 'pointer',
        // Remove webkit tap highlight
        WebkitTapHighlightColor: 'transparent'
      }}
      onClick={handleClick}
      onTouchStart={touchHandlers.onTouchStart}
      onTouchMove={touchHandlers.onTouchMove}
      onTouchEnd={touchHandlers.onTouchEnd}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label={`View details for ${show.show}`}
    >
      <ShowTile show={show} isNewShow={false} disableNavigation={true} tileSizePx={189} />
    </div>
  );
};

/**
 * HorizontalShowCarousel - Reusable horizontal scrolling carousel for shows
 * 
 * Features:
 * - Native iOS-optimized smooth scrolling
 * - Navigation arrows with conditional visibility
 * - Native touch/swipe support via browser scrolling
 * - Keyboard navigation support
 * - Responsive design with configurable shows per view
 * - Accessibility compliant with ARIA labels
 * - Performance optimized for mobile devices
 */
const HorizontalShowCarousel: React.FC<HorizontalShowCarouselProps> = ({
  shows,
  title,
  showsPerView = {
    mobile: 2,
    tablet: 3,
    desktop: 4
  },
  scrollAmount = 1,
  showArrows = true,
  className = '',
  arrowClassName = '',
  onShowClick,
  testId = 'horizontal-show-carousel'
}) => {
  // Use custom carousel hook for state management
  const {
    scrollContainerRef,
    carouselState,
    scrollLeft,
    scrollRight,
    handleScroll,
    handleKeyDown
  } = useCarousel({
    scrollAmount,
    animationDuration: 300
  });

  if (!shows.length) {
    return null;
  }

  return (
    <div 
      className={`relative ${className} focus:outline-none focus:ring-2 focus:ring-red-500/30 focus:ring-offset-2 focus:ring-offset-gray-900 rounded-lg`}
      data-testid={testId}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="region"
      aria-label={title ? `${title} carousel` : 'Shows carousel'}
      aria-describedby={`${testId}-instructions`}
    >
      {/* Skip link for accessibility */}
      <a
        href={`#${testId}-end`}
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:px-3 focus:py-2 focus:bg-red-600 focus:text-white focus:rounded focus:text-sm focus:font-medium focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-red-500/50 focus:ring-offset-2 focus:ring-offset-gray-900"
        tabIndex={0}
      >
        Skip {title || 'shows'} carousel
      </a>

      {/* Hidden instructions for screen readers */}
      <div 
        id={`${testId}-instructions`}
        className="sr-only"
      >
        Use arrow keys to navigate through shows, or tab to focus individual shows
      </div>
      {/* Optional title */}
      {title && (
        <h3 className="text-lg sm:text-xl font-semibold text-white mb-3 sm:mb-4">
          {title}
        </h3>
      )}

      {/* Carousel container */}
      <div className="relative">
        {/* Left arrow - always visible when can scroll left, with larger hit zone */}
        {showArrows && carouselState.canScrollLeft && (
          <div className="absolute -left-6 top-1/2 -translate-y-1/2 z-20 p-4 -m-4">
            <CarouselArrow
              direction="left"
              onClick={scrollLeft}
              disabled={carouselState.isScrolling}
              className={arrowClassName}
            />
          </div>
        )}

        {/* Right arrow - always visible when can scroll right, with larger hit zone */}
        {showArrows && carouselState.canScrollRight && (
          <div className="absolute -right-6 top-1/2 -translate-y-1/2 z-20 p-4 -m-4">
            <CarouselArrow
              direction="right"
              onClick={scrollRight}
              disabled={carouselState.isScrolling}
              className={arrowClassName}
            />
          </div>
        )}

        {/* Scrollable container optimized for smooth custom animations */}
        <ul
          ref={scrollContainerRef}
          className="flex gap-3 sm:gap-4 overflow-x-auto scrollbar-hide list-none"
          onScroll={handleScroll}
          aria-label={`${title || 'Shows'} carousel content`}
          aria-live="polite"
          aria-atomic="false"
          aria-relevant="additions removals"
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            // Disable native smooth scrolling - we handle animations manually
            scrollBehavior: 'auto',
            // Optimize for smooth custom animations
            scrollSnapType: 'none', // Disable snap for smoother custom scrolling
            // Enable hardware acceleration for better performance
            willChange: 'scroll-position',
            // Optimize for touch devices while maintaining smooth desktop scrolling
            WebkitOverflowScrolling: 'touch',
            // Allow both horizontal and vertical scrolling
            touchAction: 'pan-x pan-y',
            // Remove will-change as it can cause iOS performance issues
          }}
        >
          {shows.map((show, index) => (
            <li
              key={show.show_id || `${show.show}-${index}`}
              className="flex-shrink-0 w-32 sm:w-[11.8rem] md:w-[11.8rem] transition-transform duration-200 ease-out hover:scale-[1.02] active:scale-[0.98]"
              style={{
                WebkitTapHighlightColor: 'transparent',
                cursor: 'pointer',
                transform: 'translateZ(0)',
              }}
            >
              <ScrollAwareShowTile
                show={show}
                onShowClick={onShowClick}
                className="w-full"
              />
            </li>
          ))}
        </ul>
      </div>

      {/* Skip link target */}
      <div 
        id={`${testId}-end`}
        className="sr-only"
        tabIndex={-1}
        aria-label="End of carousel"
      >
        End of {title || 'shows'} carousel
      </div>
    </div>
  );
};

export default HorizontalShowCarousel;
